// One right-column module. In BodyWithAside's column (and stack) layout it
// renders its full card, `children`, unchanged. In the strip it renders a
// summary card instead: every module in the strip shares one shape — label
// at the top, a title cut to two lines (with its avatars beside it), an
// optional meta line, and its action pinned to the bottom — so the row
// reads as a set. The strip stretches the cards to one height, set by their
// content, so they grow with the text size instead of clipping it.

import { useState, type ReactNode } from 'react';
import {
  Body,
  Button,
  Caption,
  Card,
  CONTROL_HEIGHT,
  Dialog,
  Eyebrow,
  Heading,
  Row,
  Skeleton,
  Stack,
} from '@leanin/ui';

import { useAsideLayout } from './aside-layout';
import styles from './AsideModule.module.css';

export type AsideSummary =
  | {
      loading: true;
      /** The module's label, when it doesn't itself depend on the data
       *  (an event's "Going" vs "Attended"); a placeholder otherwise. */
      eyebrow?: string;
      /** Whether the loaded summary ends in an action. Its place is held
       *  only then, so the card keeps its height when the data arrives. */
      withAction: boolean;
    }
  | {
      loading?: false;
      /** The module's label, as its full card's eyebrow reads. */
      eyebrow: string;
      /** Pinned to the card's bottom edge — a Button, a Row of them, or an
       *  AsideExpand when the full module has no page of its own. */
      action?: ReactNode;
      /** Cut to two lines. */
      title?: string;
      /** Prose under the title — house rules, an empty state's sentence —
       *  cut to two lines. */
      excerpt?: string;
      /** A muted line under the text: a date, a count. */
      meta?: ReactNode;
      /** The people the module is about. With a title: an Avatar or
       *  AvatarStack on the title's row, ahead of it, so the count or name
       *  and its faces read as one line. Without one: the people themselves,
       *  e.g. a lone leader's row. */
      visual?: ReactNode;
    };

// Marks a loading summary's placeholder, for isLoadingSummary.
const LOADING_ATTRIBUTE = 'data-aside-loading';

/** Whether a strip card is a summary still waiting on its data: its
 *  placeholders say nothing about how wide its content will be. */
export function isLoadingSummary(card: Element): boolean {
  return card.querySelector(`[${LOADING_ATTRIBUTE}]`) !== null;
}

/** Whether the summary shows anything between its label and its action. */
function hasBody(summary: AsideSummary): boolean {
  if (summary.loading === true) return true;
  return [summary.title, summary.excerpt, summary.meta, summary.visual].some((part) => part !== undefined);
}

export function AsideModule({ summary, children }: { summary: AsideSummary; children: ReactNode }) {
  const layout = useAsideLayout();
  if (layout !== 'strip') return <>{children}</>;
  return (
    <Card pad="compact" className={styles['summary']}>
      <Stack gap="sm">
        {summary.eyebrow !== undefined ? (
          <Eyebrow tone="accent">{summary.eyebrow}</Eyebrow>
        ) : (
          <Skeleton height="1em" width="40%" />
        )}
        {summary.loading === true ? (
          <Stack gap="xs" {...{ [LOADING_ATTRIBUTE]: '' }}>
            <Skeleton height="1.25em" />
            <Skeleton height="1.25em" width="60%" />
          </Stack>
        ) : (
          <>
            {summary.title !== undefined && summary.visual !== undefined ? (
              <Row gap="sm" align="center" className={styles['titleRow']}>
                {summary.visual}
                <Heading level={3} lines={2}>{summary.title}</Heading>
              </Row>
            ) : summary.title !== undefined ? (
              <Heading level={3} lines={2}>{summary.title}</Heading>
            ) : (
              summary.visual
            )}
            {summary.excerpt !== undefined ? (
              <Body size="sm" lines={2}>{summary.excerpt}</Body>
            ) : null}
            {summary.meta !== undefined ? <Caption>{summary.meta}</Caption> : null}
          </>
        )}
      </Stack>
      {summary.loading === true ? (
        summary.withAction ? (
          <div className={styles['action']}>
            <Skeleton width="45%" height={CONTROL_HEIGHT.sm} rounded="full" />
          </div>
        ) : null
      ) : summary.action !== undefined ? (
        <div className={hasBody(summary) ? styles['action'] : styles['soleAction']}>
          {summary.action}
        </div>
      ) : null}
    </Card>
  );
}

/** A summary action that opens the full module in a dialog — for modules
 *  whose full content (a members list, house rules) has no page to link to.
 *  Pass the module's frameless body: the dialog supplies the title. */
export function AsideExpand({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen} title={title}>
        {children}
      </Dialog>
    </>
  );
}
